import * as amplitude from '@amplitude/analytics-browser'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  props: {
    trackEvent: {
      default: null,
      required: false,
      type: String,
    },
  },
  computed: {
    ...mapState({
      amplitudeState: (state) => state.tracking.amplitudeState,
    }),
    ...mapGetters({
      trackingAllowed: 'tracking/trackingAllowed',
    }),
  },
  methods: {
    ...mapActions({
      flushEvents: 'tracking/flushEvents',
      trackAmplitude: 'tracking/trackAmplitude',
      setAmplitudeState: 'tracking/setAmplitudeState',
    }),
    /**
     * Initialize the tracking libraries. Must be called once, after the app has been created.
     *
     * - [Amplitude](https://www.npmjs.com/package/@amplitude/analytics-browser)
     * - [Datadog](https://www.npmjs.com/package/@datadog/browser-rum)
     *
     * @return {undefined}
     */
    initTracking() {
      if (!this.trackingAllowed) {
        return
      }

      if (this.amplitudeState === 'init') {
        try {
          amplitude?.init(process.env.VUE_APP_AMPLITUDE_KEY)
          this.setAmplitudeState('ready')
        } catch (error) {
          this.setAmplitudeState('error')
        }
      }
    },
    /**
     * Sets the user for tracking. Must be called once, after the user has logged in.
     * @param {Object} user - The user
     * @param {String | Number} user.id - The user's ID
     * @param {String} user.email - The user's email
     * @param {String} user.name - The user's name
     * @return {undefined}
     */
    setTrackingUser(user) {
      if (!this.trackingAllowed) {
        return
      }

      if (this.amplitudeState === 'ready') {
        const formattedUserId = user?.id
          ? this.$helpers.padIdToString({
              id: user.id,
              padAmount: 5,
              padChars: 0,
            })
          : null
        try {
          amplitude?.setUserId(formattedUserId)
        } catch (error) {
          this.amplitudeState = 'error'
        }
      }
    },
    /**
     * Tracks an event assigned to a user
     * @param {String} name - The name of the event
     * @param {Object} [options] - The options or "additional event properties" to pass to the event
     * @param {Object} [overwrites] - Overwrite [the event options](https://amplitude.github.io/Amplitude-TypeScript/interfaces/_amplitude_analytics_browser.Types.EventOptions.html)
     * @return {undefined}
     */
    track(name, options, overwrites) {
      this.trackAmplitude({
        name,
        options,
        overwrites,
      })
    },
    /**
     * Flushes the events buffer. Used to ensure events are sent before the page is unloaded.
     * @return {Promise<undefined>}
     */
    async flushAmplitude() {
      await this.flushEvents()
    },
  },
}
